import { HttpResponse } from '@angular/common/http';
import { Component, effect, OnDestroy, OnInit, untracked } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';

import { UserService } from 'src/app/services/user.service';
import { MdrService } from 'src/app/services/mdr.service';
import { SpinnerService } from 'src/app/_components/spinner/spinner.service';
import { FileDownloadService } from 'src/app/services/file-download.service';
import { ModalErrorComponent } from 'src/app/modal/modal-gestioneErrori/modal-error/modal-error.component';
import { MdrPassengersModalComponent } from 'src/app/modal/modal-mdr/mdr-passengers-modal/mdr-passengers-modal.component';
import { MyErrorHandler } from 'src/app/constants/error';
import { MdrOpenRequest } from 'src/app/model/mdr.model';
import { Ship } from 'src/app/model';
import { MdrHistoryFilters, MdrHistoryListStore, MdrSortState } from '../stores';

@Component({
  selector: 'app-mdr-request-history',
  templateUrl: './mdr-request-history.component.html',
  styleUrls: ['./mdr-request-history.component.scss'],
  standalone: false,
  providers: [MdrHistoryListStore],
})
export class MdrRequestHistoryComponent implements OnInit, OnDestroy {

  private ngUnsubscribe = new Subject<void>();

  availableShips: Ship[] = [];
  selectedShips: Ship[] = [];

  filterPassengerName: string = '';
  filterDateRange = new FormGroup({
    start: new FormControl<any>(null),
    end: new FormControl<any>(null),
  });
  filterCabin: string = '';
  filterStatus: string = '';
  filterHistoryRestaurant: string = '';
  filterTimeChanged: string = '';

  displayedHistoryColumns: string[] = [
    'status',
    'request_datetime',
    'cabin',
    'passenger_name',
    'booking_number',
    'restaurant',
    'original_shift',
    'requested_shift',
    'total_diners',
    'ship'
  ];

  exporting: boolean = false;

  sortBy: string = 'request_datetime';
  sortOrder: 'asc' | 'desc' = 'desc';

  private storeSpinnerShown = false;

  constructor(
    private dialog: MatDialog,
    private userService: UserService,
    private mdrService: MdrService,
    private spinner: SpinnerService,
    private fileDownloadService: FileDownloadService,
    public store: MdrHistoryListStore,
  ) {
    effect(() => {
      const isLoading = this.store.loading();
      if (isLoading && !this.storeSpinnerShown) {
        this.spinner.show();
        this.storeSpinnerShown = true;
      } else if (!isLoading && this.storeSpinnerShown) {
        this.spinner.hide();
        this.storeSpinnerShown = false;
      }
    });

    effect(() => {
      const err = this.store.loadError();
      if (!err) return;
      untracked(() => {
        this.dialog.open(ModalErrorComponent, { data: MyErrorHandler.getText(err) });
        this.store.loadError.set(null);
      });
    });
  }

  ngOnInit(): void {
    this.availableShips = this.userService.ships;
    // If the user has a single ship, preselect it (the field is disabled in the template)
    if (this.availableShips.length === 1) {
      this.selectedShips = [...this.availableShips];
    }
    this.store.init(this.buildFilters(), this.buildSortState());
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  search(): void {
    this.store.setFilters(this.buildFilters());
  }

  /**
   * Re-fetches the list with the current filters/sort, bypassing the LRU page
   * cache (setFilters restarts from page 1 with a fresh network load). Called
   * by the parent shell when the user enters this tab, so the history reflects
   * accept/reject actions performed in the "manage" tab.
   */
  reload(): void {
    this.store.setFilters(this.buildFilters());
  }

  onPageChange(event: PageEvent): void {
    if (event.pageSize !== this.store.pageSize()) {
      this.store.setPageSize(event.pageSize);
      return;
    }
    this.store.goToPage(event.pageIndex + 1);
  }

  onSort(col: string): void {
    if (col === this.sortBy) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = col;
      this.sortOrder = 'asc';
    }
    this.store.setSort(this.buildSortState());
  }

  private buildFilters(): MdrHistoryFilters {
    const { date_from, date_to } = this.getDateRange();
    return {
      ships: this.selectedShips.length ? this.selectedShips.map(s => s.id_ship) : undefined,
      passenger_name: this.filterPassengerName || undefined,
      date_from,
      date_to,
      cabin: this.filterCabin || undefined,
      status: this.filterStatus || undefined,
      restaurant: this.filterHistoryRestaurant || undefined,
      time_changed: this.filterTimeChanged || undefined,
    };
  }

  private buildSortState(): MdrSortState {
    return { by: this.sortBy, order: this.sortOrder };
  }

  private getDateRange(): { date_from?: string; date_to?: string } {
    const { start, end } = this.filterDateRange.value;
    const result: { date_from?: string; date_to?: string } = {};
    if (start) result.date_from = moment(start).format('YYYY-MM-DD');
    if (end)   result.date_to   = moment(end).format('YYYY-MM-DD');
    return result;
  }

  openPassengersModal(row: MdrOpenRequest): void {
    this.spinner.show();
    this.mdrService.getMdrPassengers(row.mdr_request_id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          this.dialog.open(MdrPassengersModalComponent, {
            data: { ...res, booking_number: row.booking_number, passenger_name: row.passenger_name },
            width: '1400px',
            autoFocus: 'dialog'
          });
        },
        error: (err) => {
          this.spinner.hide();
          this.dialog.open(ModalErrorComponent, {
            data: MyErrorHandler.getText(err)
          });
        }
      });
  }

  exportData(): void {
    if (this.exporting) return;
    this.exporting = true;

    const { date_from, date_to } = this.getDateRange();

    const exportObservable$: Observable<HttpResponse<Blob>> = this.mdrService.exportMdrHistoryRequests(
      this.selectedShips.length ? this.selectedShips.map(s => s.id_ship) : undefined,
      this.filterPassengerName || undefined,
      date_from,
      date_to,
      this.filterCabin || undefined,
      this.sortBy,
      this.sortOrder,
      this.filterStatus || undefined,
      this.filterHistoryRestaurant || undefined,
      this.filterTimeChanged || undefined
    );

    exportObservable$
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe({
      next: (res) => {
        this.fileDownloadService.downloadFromResponse(res, 'mdr_history_requests.xlsx');
        this.exporting = false;
      },
      error: async (err) => {
        this.exporting = false;
        let parsed: any = err;
        if (err?.error instanceof Blob) {
          try {
            const txt = await err.error.text();
            parsed = { ...err, error: txt ? JSON.parse(txt) : null };
          } catch { /* keep err */ }
        }
        this.dialog.open(ModalErrorComponent, {
          data: MyErrorHandler.getText(parsed)
        });
      }
    });
  }

  getStatusLabel(status?: string): string {
    if (status === 'MDR_ACCEPTED') return 'Accepted';
    if (status === 'MDR_REJECTED') return 'Rejected';
    return status || '-';
  }
}
